import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  Box,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Avatar,
  styled,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  LocationOn,
  CheckCircle,
  Schedule,
  HourglassEmpty,
  Cancel,
  BuildOutlined,
  ArrowBackIos,
  ArrowForwardIos,
} from '@mui/icons-material';
import { getProject, resolveImageUrl } from '../utils/api';

const ImageSlider = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%', // Default fallback
  [theme.breakpoints.up('sm')]: {
    width: '70%',
  },
  height: 300,
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  // marginLeft: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const SliderImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const SliderButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
}));

const SliderIndicators = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: theme.spacing(0.5),
  zIndex: 1,
}));

const SliderDot = styled(Box)(({ theme, active }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: active ? 'white' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
}));

const ProjectImageSlider = ({ images }) => {
  const [currentImage, setCurrentImage] = useState(0);

  if (!images || images.length === 0) {
    return (
      <ImageSlider>
        <SliderImage
          src="/placeholder-project.jpg"
          alt="No images available"
        />
      </ImageSlider>
    );
  }


  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentImage(index);
  };

  return (
    <ImageSlider>
      <SliderImage
        src={resolveImageUrl(images[currentImage]?.image_url || images[currentImage])}
        alt={`Project image ${currentImage + 1}`}
      />

      {images.length > 1 && (
        <>
          <SliderButton
            onClick={prevImage}
            sx={{ left: 8 }}
          >
            <ArrowBackIos />
          </SliderButton>

          <SliderButton
            onClick={nextImage}
            sx={{ right: 8 }}
          >
            <ArrowForwardIos />
          </SliderButton>

          <SliderIndicators>
            {images.map((_, index) => (
              <SliderDot
                key={index}
                active={index === currentImage}
                onClick={() => goToImage(index)}
              />
            ))}
          </SliderIndicators>
        </>
      )}
    </ImageSlider>
  );
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProject();
  }, [id]);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await getProject(id); // Now supports both slug and id

      if (response.data && response.data.success) {
        setProject(response.data.project);
      } else {
        setError('Project not found');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '' || dateString === '0000-00-00' || dateString === null) {
      return null;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleDateString();
  };

  const getProjectStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return 'warning';
      case 'pending':
      case 'planning':
      case 'upcoming': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getProjectStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle />;
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return <Schedule />;
      case 'pending':
      case 'planning':
      case 'upcoming': return <HourglassEmpty />;
      case 'cancelled': return <Cancel />;
      default: return <HourglassEmpty />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !project) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Project not found'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/projects')}
        >
          Back to Projects
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          Back to Projects
        </Button>

        <Typography variant="h3" component="h1" gutterBottom sx={{
          fontSize: {
            xs: '1.2rem',   // mobile
            sm: '1.5rem', // tablet
            md: '1.8rem',   // desktop
            lg: '2.2rem', // large screens
          },
        }}>
          {project.title}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getProjectStatusIcon(project.status)}
            <Chip
              label={project.status || 'Unknown'}
              color={getProjectStatusColor(project.status)}
              variant="outlined"
            />
          </Box>
          {project.category && (
            <Chip
              label={project.category}
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, color: 'text.secondary' }}>
          {project.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOn sx={{ fontSize: 20 }} />
              <Typography variant="body2">{project.location}</Typography>
            </Box>
          )}

          {project.status?.toLowerCase() === 'completed' && formatDate(project.completion_date) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 20 }} />
              <Typography variant="body2">
                Completed: {formatDate(project.completion_date)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Project Details */}
        <Grid item xs={12} md={8}>
          <Box >
            <Typography variant="body1" paragraph>
              {project.description}
            </Typography>
          </Box>

          <Box
            key={project.id}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 3,
              mb: 4,
              maxWidth: '100%',
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            {/* Project Image Slider - full width */}
            <Box sx={{ mb: 2, width: '100%' }}>
              <ProjectImageSlider images={project.images || []} />
            </Box>
          </Box>
          <Box sx={{ mb: 4 }}>
            {/* Additional Images Gallery */}
            {project.images && project.images.length > 1 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Project Gallery ({project.images.length} images)
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {project.images.map((image, index) => (
                    <Grid item xs={4} key={index}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 4
                          }
                        }}
                        onClick={() => {
                          // Open in new tab for full view
                          window.open(resolveImageUrl(image.image_url), '_blank');
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="150"
                          image={resolveImageUrl(image.image_url)}
                          alt={`${project.title} - Image ${index + 1}`}
                          sx={{ objectFit: 'cover' }}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </Box>
        </Grid>
        
        {/* Main Image */}
        <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Card sx={{ mb: 3 }}>
            <CardMedia
              component="img"
              height="400"
              image={resolveImageUrl(project.image_url)}
              alt={project.title}
              sx={{ objectFit: 'cover' }}
            />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Services provided in this project
            </Typography>
            {project.services && project.services.length > 0 ? (
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
              }}>
                {project.services.map(service => (
                  <Chip
                    key={service.id}
                    icon={
                      service.icon_url ? (
                        <Avatar
                          src={resolveImageUrl(service.icon_url)}
                          sx={{ width: 24, height: 24, background: 'transparent' }}
                        />
                      ) : (
                        <BuildOutlined sx={{ color: 'primary.main' }} />
                      )
                    }
                    label={
                      <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                        {service.title}
                      </Typography>
                    }
                    color="primary"
                    variant="outlined"
                    component="button"
                    onClick={() => navigate(`/services/${service.slug || service.id}`)}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      background: 'linear-gradient(90deg, #e0eafc 0%, #cfdef3 100%)',
                      boxShadow: 1,
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s',
                      '&:hover': {
                        boxShadow: 4,
                        background: 'linear-gradient(90deg, #d2e7fa 0%, #b5d0ea 100%)',
                      },
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No services linked to this project.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProjectDetail;
